import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import _ from 'lodash';
import { useSelector } from 'react-redux';
import { HorizontalBar } from 'react-chartjs-2';
import { Dropdown } from 'react-bootstrap';
import Table from 'react-bootstrap/Table';
import { useTranslation } from 'react-i18next';
import { LoadingState } from '../../../utilities/constants';
import Empty from '../../../components/Empty';
import ReportContainer from '../ReportContainer';
import { fetchGHGSnapshotReport } from '../reportsSlice';
import {
  formatNumber,
  createInitChartConfig,
  generateChartLegend,
  getColorForKey,
  parseScopeNumber,
  orderByWeightThenLabel,
  generateRandomColors,
  hasUncategorizedCategory,
  wrapTooltipText,
  getCurrentReportPeriod,
  getReportRangePeriod,
  getCommonFilterPeriodRange,
} from '../reportHelper';

const GHGSnapshotReport = () => {
  const { t } = useTranslation();
  const userInfo = useSelector((state) => state.users.user);
  const ghgSnapshotReport = useSelector((state) => state.reports.ghgSnapshotReport);
  const ghgSnapshotReportStatus = useSelector((state) => state.reports.ghgSnapshotReportStatus);
  const [viewMode, setViewMode] = useState('absolute');
  const chartRef = useRef(null);

  const currentReportPeriod = getCurrentReportPeriod(userInfo.usrOrgOptions);
  const reportRangePeriod = getReportRangePeriod(userInfo.usrOrgOptions, currentReportPeriod.reportYear);
  const getFilterPeriodRangeHandler = (data) => getCommonFilterPeriodRange(data, reportRangePeriod);

  const fetchGHGSnapshotReportHandler = async (dispatch, payload) => {
    const entitiesIdsToInclude = payload.data.entitiesIdsToInclude?.length
      ? payload.data.entitiesIdsToInclude
      : [];
    return dispatch(fetchGHGSnapshotReport({
      orgId: payload.orgId,
      data: {
        ...payload.data,
        entitiesIdsToInclude,
        isApplyExcludedEPSs: true,
      },
    }));
  };
  generateRandomColors(true);
  const { scopeRows, categoryNames, totalOrganisationEmissions } = useMemo(() => {
    const rows = _.orderBy(
      (ghgSnapshotReport || [])
        .map((item) => ({
          scopeNumber: parseScopeNumber(item.scope),
          records: (item.records || []).filter((record) => Number(record.co2_e_t) !== 0),
        }))
        .filter((item) => item.scopeNumber !== null && item.records.length > 0),
      'scopeNumber',
      'asc'
    );

    // Categories/EPS follow the org's configured weight when set (matching the tree-ordering
    // convention in src/utilities/treeHelpers.js), falling back to alphabetical otherwise.
    const categoryMeta = _.uniqBy(
      rows.flatMap((row) => row.records.map((record) => ({ label: record.category, weight: record.category_weight }))),
      'label'
    );
    const categories = orderByWeightThenLabel(categoryMeta).map((item) => item.label);

    const rowsWithTotals = rows.map((row) => {
      const recordsByCategory = _.groupBy(row.records, 'category');
      const orderedRecords = categories.flatMap((categoryName) =>
        orderByWeightThenLabel(recordsByCategory[categoryName] || [], 'eps', 'eps_weight')
      );
      return {
        scopeName: `Scope ${row.scopeNumber}`,
        scopeNumber: row.scopeNumber,
        records: orderedRecords,
        totalCo2e: _.sumBy(row.records, (record) => Number(record.co2_e_t)),
        totalPer: _.sumBy(row.records, (record) => Number(record.per_co2_e_t)),
      };
    });

    return {
      scopeRows: rowsWithTotals,
      categoryNames: categories,
      totalOrganisationEmissions: _.sumBy(rowsWithTotals, 'totalCo2e'),
    };
  }, [ghgSnapshotReport]);

  const chartDatasets = useMemo(
    () =>
      categoryNames.map((categoryName) => {
        const absoluteData = [];
        const percentData = [];
        scopeRows.forEach((row) => {
          const matching = row.records.filter((record) => record.category === categoryName);
          absoluteData.push(_.sumBy(matching, (record) => Number(record.co2_e_t)));
          percentData.push(_.sumBy(matching, (record) => Number(record.per_co2_e_t)));
        });
        return {
          label: categoryName,
          backgroundColor: getColorForKey(categoryName),
          data: viewMode === 'absolute' ? absoluteData : percentData,
          absoluteData,
        };
      }),
    [categoryNames, scopeRows, viewMode]
  );

  const chartData = useMemo(
    () => ({ labels: scopeRows.map((row) => row.scopeName), datasets: chartDatasets }),
    [scopeRows, chartDatasets]
  );

  const tooltipLines = useCallback(
    (tooltipItem, data) => {
      const dataset = data.datasets[tooltipItem.datasetIndex];
      const text =
        viewMode === 'proportional'
          ? t('{{category}}: {{value}}%', {
              category: dataset.label,
              value: formatNumber(dataset.data[tooltipItem.index]),
            })
          : t('{{category}}: {{value}} tCO2e', {
              category: dataset.label,
              value: formatNumber(dataset.absoluteData[tooltipItem.index]),
            });
      return wrapTooltipText(text);
    },
    [t, viewMode]
  );

  const options = useMemo(
    () =>
      createInitChartConfig({
        maintainAspectRatio: false,
        scales: {
          xAxes: [
            {
              stacked: true,
              ticks: { beginAtZero: true },
              scaleLabel: {
                display: true,
                labelString: viewMode === 'absolute' ? t('CO2-e (t)') : t('Contribution (%)'),
              },
            },
          ],
          yAxes: [
            {
              stacked: true,
              gridLines: { display: false },
            },
          ],
        },
        tooltips: {
          filter: (tooltipItem, data) => data.datasets[tooltipItem.datasetIndex].data[tooltipItem.index] !== 0,
          callbacks: {
            title: (tooltipItems, data) => data.labels[tooltipItems[0].index],
            label: (tooltipItem, data) => tooltipLines(tooltipItem, data)[0],
            afterLabel: (tooltipItem, data) => tooltipLines(tooltipItem, data).slice(1),
          },
        },
      }),
    [t, viewMode, tooltipLines]
  );

  const showUncategorizedNote = useMemo(() => hasUncategorizedCategory(categoryNames), [categoryNames]);

  const viewModeOptions = [
    { value: 'absolute', label: t('Absolute tCO2e') },
    { value: 'proportional', label: t('Proportional Contribution (%)') },
  ];

  const renderViewModeDropdown = () => (
    <Dropdown className="mr-3" onSelect={(key) => setViewMode(key)}>
      <Dropdown.Toggle
        variant="secondary"
        className="d-flex align-items-center justify-content-center"
        disabled={!hasData}
      >
        {viewModeOptions.find((option) => option.value === viewMode)?.label}
      </Dropdown.Toggle>
      <Dropdown.Menu>
        {viewModeOptions.map((option) => (
          <Dropdown.Item key={option.value} eventKey={option.value} active={option.value === viewMode}>
            {option.label}
          </Dropdown.Item>
        ))}
      </Dropdown.Menu>
    </Dropdown>
  );

  const hasData = ghgSnapshotReportStatus === LoadingState.succeeded && scopeRows.length > 0;

  useEffect(() => {
    if (hasData) {
      generateChartLegend(chartRef);
    }
  }, [hasData, chartData]);

  const exportDataHandler = (type) => {
    if (type !== 'csv') {
      return null;
    }
    const exportData = [
      [t('Scope'), t('Category'), t('EPS Name'), t('CO2-e (t)'), t('% Total CO2-e (t)')],
      [t('Total:'), '', '', formatNumber(totalOrganisationEmissions), formatNumber(100)],
    ];
    scopeRows.forEach((row) => {
      row.records.forEach((record) => {
        exportData.push([
          row.scopeNumber,
          record.category,
          record.eps,
          formatNumber(record.co2_e_t),
          formatNumber(record.per_co2_e_t),
        ]);
      });
      exportData.push([
        t('Scope {{scope}} Sub-total:', { scope: row.scopeNumber }),
        '',
        '',
        formatNumber(row.totalCo2e),
        formatNumber(row.totalPer),
      ]);
    });
    return exportData;
  };

  const chartRender = () => {
    if (!hasData) {
      return <Empty title={t('No emissions data available for the selected filters.')} />;
    }
    return (
      <>
        <div className="chart-graph">
          <HorizontalBar data={chartData} options={options} ref={chartRef} />
        </div>
        <div id="js-legend" className="chart-legend" style={{ height: 'auto', padding: '0px'}}></div>
        {viewMode === 'absolute' && (
          <p className="mt-3 mb-0">
            <strong>
              {t('Total Organisation Emissions: {{value}} tCO2e', { value: formatNumber(totalOrganisationEmissions) })}
            </strong>
          </p>
        )}
        {showUncategorizedNote && (
          <p className="mt-3 mb-0 font-italic text-muted">
            {t('Note: Any EPS not assigned to a sub-category will appear as “Uncategorized”.')}
          </p>
        )}
        <Table bordered hover id="ghg-snapshot-table" className="mt-4">
          <thead>
            <tr>
              <th>{t('Scope')}</th>
              <th>{t('Category')}</th>
              <th>{t('EPS Name')}</th>
              <th>{t('CO2-e (t)')}</th>
              <th>{t('% Total CO2-e (t)')}</th>
            </tr>
          </thead>
          <tbody>
            <tr style={{ backgroundColor: '#004866', color: '#FFFFFF' }}>
              <td>{t('Total:')}</td>
              <td></td>
              <td></td>
              <td>{formatNumber(totalOrganisationEmissions)}</td>
              <td>{formatNumber(100)}</td>
            </tr>
            {scopeRows.map((row) => (
              <React.Fragment key={row.scopeName}>
                {row.records.map((record, idx) => (
                  <tr key={`${row.scopeName}-${record.category}-${record.eps}-${idx}`}>
                    <td>{row.scopeNumber}</td>
                    <td>{record.category}</td>
                    <td>{record.eps}</td>
                    <td>{formatNumber(record.co2_e_t)}</td>
                    <td>{formatNumber(record.per_co2_e_t)}</td>
                  </tr>
                ))}
                <tr style={{ backgroundColor: '#004866', color: '#FFFFFF' }}>
                  <td>{t('Scope {{scope}} Sub-total:', { scope: row.scopeNumber })}</td>
                  <td></td>
                  <td></td>
                  <td>{formatNumber(row.totalCo2e)}</td>
                  <td>{formatNumber(row.totalPer)}</td>
                </tr>
              </React.Fragment>
            ))}
          </tbody>
        </Table>
      </>
    );
  };

  return (
    <ReportContainer
      reportName={t('GHG Snapshot Report')}
      reportSubTitle={t('GHG Snapshot Report')}
      fetchReportData={fetchGHGSnapshotReportHandler}
      fetchReportStatus={ghgSnapshotReportStatus}
      reportData={ghgSnapshotReport}
      reportRender={chartRender}
      chartRef={chartRef}
      beforeConfigureRender={renderViewModeDropdown}
      getFilterPeriodRange={getFilterPeriodRangeHandler}
      exportDataHandler={exportDataHandler}
    />
  );
};

export default GHGSnapshotReport;
